/**
 * The pieces «Medlemmer» draws in more than one place (2026-10-15). Each was written two or three times — a member
 * search in Members.tsx and another in Register.tsx, the app account's identity in three tables, the link preview and
 * the "how do you know it is them" select in both the link and the approval — and the copies had started to differ.
 */
import React, { useEffect, useRef, useState } from 'react';
import { call, type Body } from '../lib/api';
import { Field, Tag } from './ui';
import { fieldErrorToast } from '../lib/toast';

/** A field error also goes up as a toast (2026-10-31). Draws nothing. */
function ToastField({ text }: { text?: string }) { React.useEffect(() => { if (text) fieldErrorToast(text); }, [text]); return null; }
import { STANDING, type Standing } from '../lib/membership-status';

export const PROVIDER_NAME: Record<string, string> = { bankid: 'BankID', vipps: 'Vipps', sms: 'SMS' };

/**
 * A person picker over the register: ten at most, no phone or e-mail, names never logged.
 *
 * QA C (2026-10-16): a failed search said «Ingen treff.» (as if nobody matched), and a slow answer to «ah» could arrive
 * after the answer to «ahmed» and replace it. Now a failure is said, with the reason, and only the answer to the
 * newest query is drawn (`latest`). The results are a list of real buttons (they were buttons with role=listitem,
 * which a screen reader then did not offer as buttons).
 */
export function PersonSearch({ label, onPick }: { label: string; onPick: (m: Body) => void }) {
  const [q, setQ] = useState('');
  const [items, setItems] = useState<Body[] | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const latest = useRef(0);
  useEffect(() => {
    const v = q.trim();
    const n = ++latest.current;
    if (v.length < 2) { setItems(null); setFailed(null); return; }
    const t = setTimeout(async () => {
      try {
        const r = (await call('/members/search?q=' + encodeURIComponent(v))).items || [];
        if (n === latest.current) { setItems(r); setFailed(null); }
      } catch (e) {
        if (n === latest.current) { setItems(null); setFailed((e as Error).message); }
      }
    }, 220);
    return () => clearTimeout(t);
  }, [q]);
  return <>
    <Field label={label}>{(id) => <input id={id} autoComplete="off" placeholder="Navn eller medlemsnummer" value={q} onChange={(e) => setQ(e.target.value)} style={{ minWidth: 240 }} />}</Field>
    {failed && <p className="msg err" role="alert">Søket virket ikke: {failed} Prøv igjen om litt.</p>}
    {items && (items.length
      ? <ul className="hh-res" aria-label="Treff">{items.map((m) => (
        <li key={m.id}><button type="button" onClick={() => { setItems(null); setQ(''); onPick(m); }}>
          {m.name} · medl. {m.memberNo || m.solidusNo}{m.birthYear ? ' · f. ' + m.birthYear : ''}{m.source === 'native' && <> <Tag tone="grey">registrert her</Tag></>}{m.ended && <> <Tag tone="grey">avsluttet</Tag></>}</button></li>))}</ul>
      : <p className="msg" role="status">Ingen treff. Prøv en annen stavemåte, eller medlemsnummeret.</p>)}
  </>;
}

/**
 * Who an app account is, as far as the panel can say: the name and birth year the sign-in gave (with the provider),
 * else the phone, else the e-mail. With a registration (what an applicant typed), that name leads.
 */
export function AccountIdentity({ account, registration, exists = true }: { account?: Body | null; registration?: Body | null; exists?: boolean }) {
  if (!exists || !account) return <Tag tone="red">kontoen er slettet</Tag>;
  const idn = account.identity;
  if (registration) return <>{registration.firstName} {registration.lastName}</>;
  if (idn && idn.name) return <>{idn.name}{idn.birthYear ? ' · f. ' + idn.birthYear : ''} <Tag tone="grey">{PROVIDER_NAME[idn.provider] || idn.provider}</Tag></>;
  if (account.phone) return <>{account.phone}</>;
  if (account.email) return <>{account.email} <Tag tone="grey">e-post</Tag></>;
  return <>—</>;
}

/** What a link (or an approval by link) will let the person see — on screen before the button (CA-39). */
export function LinkPreview({ pv }: { pv: Body }) {
  return <>
    <div><b>{pv.name}</b> · medl. {pv.memberNo || pv.solidusNo}. Personen får se:</div>
    <ul style={{ margin: '6px 0 6px 18px' }}>{(pv.grants_no || []).map((g: string, i: number) => <li key={i}>{g}</li>)}</ul>
    {pv.warning_no && <div style={{ color: 'var(--gold-text)' }}>{pv.warning_no}</div>}
  </>;
}

/** «Hvordan vet du at det er riktig person?» — the same select for a link and an approval. */
export function MethodField({ methods, value, onChange, selectRef, error, errId }: {
  methods: Body[]; value: string; onChange: (v: string) => void;
  selectRef?: React.Ref<HTMLSelectElement>; error?: string; errId?: string;
}) {
  return <Field label="Hvordan vet du at det er riktig person?" required style={{ margin: 0, minWidth: 280 }}>{(id) => <>
    <select id={id} ref={selectRef} value={value} aria-invalid={error ? true : undefined} aria-describedby={error ? errId : undefined}
      onChange={(e) => onChange(e.target.value)}><option value="">Velg …</option>{methods.map((m) => <option key={m.id} value={m.id}>{m.label_no}</option>)}</select>
    {error && <span className="msg err" id={errId} style={{ display: 'block', marginTop: 4 }}>{error}</span>}<ToastField text={error} /></>}</Field>;
}

/** A membership state as a tag, in the one vocabulary (lib/membership-status). */
export function StandingTag({ s }: { s: Standing }) {
  const [word, tone] = STANDING[s];
  return <Tag tone={tone}>{word}</Tag>;
}
